import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Paper, Grid, Chip, Button, Box,
  Divider, CircularProgress,
  Breadcrumbs, Link, IconButton, Card, CardContent, CardHeader,
  Stack, Tooltip, Fade, LinearProgress, TextField,
  Tabs, Tab, Table, TableBody, TableCell, TableRow, Avatar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TerminalIcon from '@mui/icons-material/Terminal';
import SettingsIcon from '@mui/icons-material/Settings';
import ExtensionIcon from '@mui/icons-material/Extension';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import EditServerDialog from './EditServerDialog';
import CurseForgeBrowser from './CurseForgeBrowser';
import FileManager from './FileManager';
import BackupManager from './BackupManager';
import api, {
  getServer, getServerState, uploadMod, deleteServer, startServer, stopServer, deleteMod,
  getServerStats, sendConsoleCommand, uploadDatapack,
  addWhitelistUser, removeWhitelistUser, addOpUser, removeOpUser,
  getWhitelist, getOps
} from '../api';
import { DEFAULT_SERVER_SETTINGS, ESSENTIAL_KEYS } from '../constants';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ServerDashboard() {
  const { serverName } = useParams();
  const navigate = useNavigate();
  const [server, setServer] = useState(null);
  const [status, setStatus] = useState('unknown');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [curseForgeOpen, setCurseForgeOpen] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState('Copy address');

  // Tabs layout
  const [tabValue, setTabValue] = useState(0);

  // Whitelist and Ops states
  const [whitelistUsers, setWhitelistUsers] = useState([]);
  const [opUsers, setOpsUsers] = useState([]);

  // Console Command states
  const [commandText, setCommandText] = useState('');
  const [commandSubmitting, setCommandSubmitting] = useState(false);
  const [datapackUploading, setDatapackUploading] = useState(false);

  // Access Control states
  const [whitelistUser, setWhitelistUser] = useState('');
  const [opUser, setOpUser] = useState('');
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [opLoading, setOpLoading] = useState(false);

  const logEndRef = useRef(null);
  const logContainerRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await getServerState(serverName);
      setStatus(data.state);
    } catch (e) {
      setStatus('error');
    }
  }, [serverName]);

  const fetchStats = useCallback(async () => {
    if (status !== 'running') {
      setStats(null);
      return;
    }
    try {
      const { data } = await getServerStats(serverName);
      setStats(data);
    } catch (e) {
      console.error("Failed to fetch server stats:", e);
    }
  }, [serverName, status]);

  const fetchLists = useCallback(async () => {
    try {
      const [wlRes, opRes] = await Promise.all([
        getWhitelist(serverName),
        getOps(serverName)
      ]);
      setWhitelistUsers(wlRes.data);
      setOpsUsers(opRes.data);
    } catch (e) {
      console.error("Failed to fetch access lists:", e);
    }
  }, [serverName]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await getServer(serverName);
      setServer(data);
      await fetchStatus();
      await fetchLists();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [serverName, fetchStatus, fetchLists]);

  useEffect(() => {
    fetchData();
    const statusInterval = setInterval(fetchStatus, 5000);
    return () => clearInterval(statusInterval);
  }, [fetchData, fetchStatus]);

  useEffect(() => {
    fetchStats();
    const statsInterval = setInterval(fetchStats, 5000);
    return () => clearInterval(statsInterval);
  }, [fetchStats]);

  // Log Streaming Effect
  useEffect(() => {
    const controller = new AbortController();
    let reader = null;

    const streamLogs = async () => {
      if (status !== 'running') return;

      try {
        const baseURL = api.defaults.baseURL || '';
        const response = await fetch(`${baseURL}/servers/${serverName}/logs`, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache',
            'Accept': 'text/event-stream',
          },
        });
        if (!response.body) return;

        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete line in buffer

          const newLines = lines
            .map(l => l.startsWith('data:') ? l.substring(5).trim() : l.trim())
            .filter(l => l);

          if (newLines.length > 0) {
            setLogs(prev => [...prev, ...newLines].slice(-100));
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error("Log stream error:", err);
      }
    };

    streamLogs();

    return () => {
      controller.abort();
      if (reader) reader.cancel().catch(() => {});
    };
  }, [serverName, status]);

  useEffect(() => {
    if (autoScroll) {
      logEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll]);

  const handleScroll = () => {
    if (!logContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      await action(serverName);
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteServer = async () => {
    if (!window.confirm("Are you sure you want to delete this server? This action cannot be undone.")) return;
    setActionLoading(true);
    try {
      await deleteServer(serverName);
      navigate('/');
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
      setActionLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      await uploadMod(serverName, file);
      alert('Mod uploaded! Restart server to apply.');
      fetchData();
    } catch (e) {
      alert('Upload failed');
    }
  };

  const handleDatapackUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setDatapackUploading(true);
    try {
      await uploadDatapack(serverName, file);
      alert(`Datapack ${file.name} uploaded successfully!`);
    } catch (e) {
      alert(`Datapack upload failed: ${e.response?.data || e.message}`);
    } finally {
      setDatapackUploading(false);
    }
  };

  const handleDeleteMod = async (fileName) => {
    if (!window.confirm(`Delete mod ${fileName}?`)) return;
    try {
      await deleteMod(serverName, fileName);
      fetchData();
    } catch (e) {
      alert('Failed to delete mod');
    }
  };

  const handleCopyAddress = () => {
    if (server.address) {
      navigator.clipboard.writeText(server.address);
      setCopyTooltip('Copied!');
      setTimeout(() => setCopyTooltip('Copy address'), 2000);
    }
  };

  const handleSendCommand = async (e) => {
    e.preventDefault();
    if (!commandText.trim() || commandSubmitting) return;

    const cmd = commandText.trim();
    setCommandText('');
    setCommandSubmitting(true);
    setLogs(prev => [...prev, `> ${cmd}`]);

    try {
      const { data } = await sendConsoleCommand(serverName, cmd);
      if (data.output) {
        const outputLines = data.output.split('\n').filter(l => l);
        setLogs(prev => [...prev, ...outputLines]);
      } else {
        setLogs(prev => [...prev, 'Command executed successfully.']);
      }
    } catch (err) {
      setLogs(prev => [...prev, `Error: ${err.response?.data || err.message}`]);
    } finally {
      setCommandSubmitting(false);
    }
  };

  const handleAddWhitelist = async (e) => {
    e.preventDefault();
    if (!whitelistUser.trim() || whitelistLoading) return;
    setWhitelistLoading(true);
    const user = whitelistUser.trim();
    try {
      await addWhitelistUser(serverName, user);
      setWhitelistUser('');
      setLogs(prev => [...prev, `[Console] Whitelisted player: ${user}`]);
      fetchLists();
    } catch (err) {
      alert(`Failed to whitelist user: ${err.response?.data || err.message}`);
    } finally {
      setWhitelistLoading(false);
    }
  };

  const handleRemoveWhitelist = async (user) => {
    if (!window.confirm(`Remove ${user} from whitelist?`)) return;
    try {
      await removeWhitelistUser(serverName, user);
      setLogs(prev => [...prev, `[Console] Removed whitelisted player: ${user}`]);
      fetchLists();
    } catch (err) {
      alert(`Failed to remove whitelisted user: ${err.message}`);
    }
  };

  const handleAddOp = async (e) => {
    e.preventDefault();
    if (!opUser.trim() || opLoading) return;
    setOpLoading(true);
    const user = opUser.trim();
    try {
      await addOpUser(serverName, user);
      setOpUser('');
      setLogs(prev => [...prev, `[Console] Promoted player to Operator: ${user}`]);
      fetchLists();
    } catch (err) {
      alert(`Failed to promote user: ${err.response?.data || err.message}`);
    } finally {
      setOpLoading(false);
    }
  };

  const handleRemoveOp = async (user) => {
    if (!window.confirm(`Demote operator ${user}?`)) return;
    try {
      await removeOpUser(serverName, user);
      setLogs(prev => [...prev, `[Console] Demoted player: ${user}`]);
      fetchLists();
    } catch (err) {
      alert(`Failed to demote operator: ${err.message}`);
    }
  };

  const handleKickPlayer = async (player) => {
    if (!window.confirm(`Kick player ${player}?`)) return;
    try {
      await sendConsoleCommand(serverName, `kick ${player} "Kicked from Goku MC Manager"`);
      alert(`Kicked player ${player}`);
    } catch (e) {
      alert(`Failed to kick: ${e.message}`);
    }
  };

  const handleBanPlayer = async (player) => {
    if (!window.confirm(`Ban player ${player}?`)) return;
    try {
      await sendConsoleCommand(serverName, `ban ${player}`);
      alert(`Banned player ${player}`);
    } catch (e) {
      alert(`Failed to ban: ${e.message}`);
    }
  };

  if (loading) return <Container sx={{ mt: 10, textAlign: 'center' }}><CircularProgress color="success" /></Container>;
  if (!server) return <Container sx={{ mt: 5 }}><Typography>Server not found</Typography></Container>;

  const displaySettings = [];
  ESSENTIAL_KEYS.forEach(key => {
    const val = server.server_settings[key] || DEFAULT_SERVER_SETTINGS[key];
    displaySettings.push([key, val]);
  });

  Object.entries(server.server_settings).forEach(([key, val]) => {
    if (ESSENTIAL_KEYS.includes(key)) return;
    if (val === "" || val === "false") return;
    if (val === DEFAULT_SERVER_SETTINGS[key]) return;
    displaySettings.push([key, val]);
  });

  const getStatusColor = (s) => {
    switch (s) {
      case 'running': return '#00E676';
      case 'exited': return '#FF1744';
      default: return '#FF9100';
    }
  };

  const statusColor = getStatusColor(status);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 5 }}>
      <Fade in={true} timeout={400}>
        <Box>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
            <Link component={RouterLink} underline="hover" color="inherit" to="/" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>
              Servers
            </Link>
            <Typography color="text.primary" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>{serverName}</Typography>
          </Breadcrumbs>

          <Grid container spacing={3}>
            {/* Header Block */}
            <Grid item xs={12}>
              <Paper sx={{
                p: 3.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'linear-gradient(135deg, rgba(20,20,35,0.8) 0%, rgba(10,10,20,0.5) 100%)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                position: 'relative'
              }}>
                <Box sx={{
                  height: '100%',
                  width: '4px',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bgcolor: statusColor,
                  borderRadius: '4px 0 0 4px',
                  boxShadow: `0 0 15px ${statusColor}`
                }} />

                <Box>
                  <Typography variant="h4" sx={{ fontWeight: '900', mb: 1, fontFamily: 'Outfit, sans-serif' }}>{server.name}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={server.server_settings.TYPE || DEFAULT_SERVER_SETTINGS.TYPE}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      sx={{ borderWeight: '1.5px', height: 22 }}
                    />
                    <Typography variant="subtitle2" color="text.secondary">
                      v{server.server_settings.VERSION || DEFAULT_SERVER_SETTINGS.VERSION}
                    </Typography>
                  </Stack>
                  {server.address && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{
                        fontFamily: 'Fira Code, monospace',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        p: 0.6,
                        px: 1.5,
                        borderRadius: 2,
                        fontSize: '0.8rem',
                        border: '1px solid rgba(255,255,255,0.05)',
                        color: 'secondary.light'
                      }}>
                        {server.address}
                      </Typography>
                      <Tooltip title={copyTooltip}>
                        <IconButton size="small" onClick={handleCopyAddress} sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Chip
                    label={status.toUpperCase()}
                    sx={{
                      mb: 1.5,
                      fontWeight: '800',
                      bgcolor: `${statusColor}15`,
                      color: statusColor,
                      border: `1px solid ${statusColor}30`,
                      boxShadow: `0 0 10px ${statusColor}10`,
                      px: 1
                    }}
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    DOCKER CONTAINER: {server.container_id?.substring(0, 12)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Live Metrics Grid */}
            {status === 'running' && stats && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>
                          CPU LOAD UTILIZATION
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, mb: 1, fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', color: stats.cpu_percentage > 85 ? 'error.main' : 'primary.main' }}>
                          {stats.cpu_percentage}%
                        </Typography>
                        <LinearProgress variant="determinate" value={Math.min(stats.cpu_percentage, 100)} color={stats.cpu_percentage > 80 ? "error" : "primary"} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.05)' }} />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>
                          PHYSICAL MEMORY FOOTPRINT
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, mb: 1, fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', color: 'secondary.main' }}>
                          {stats.memory_usage_mb} MB
                        </Typography>
                        <LinearProgress variant="determinate" value={Math.min((stats.memory_usage_mb / stats.memory_limit_mb) * 100, 100)} color="secondary" sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.05)' }} />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>
                          PLAYERS CONCURRENT
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, mb: 1.5, fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', color: '#00E676' }}>
                          {stats.players?.online || 0} / {stats.players?.max || 20}
                        </Typography>
                        <Box sx={{ minHeight: 8 }}>
                          {stats.players?.list && stats.players.list.length > 0 ? (
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                              {stats.players.list.map((player, idx) => (
                                <Chip key={idx} label={player} size="small" sx={{ bgcolor: 'rgba(0,230,118,0.1)', color: '#00E676', border: '1px solid rgba(0,230,118,0.2)' }} />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No players active</Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Sidebar actions: Control Panel */}
            <Grid item xs={12} lg={3}>
              <Stack spacing={3}>
                <Card sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                  <CardHeader title="Control Panel" titleTypographyProps={{ variant: 'subtitle1', sx: { fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' } }} />
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleAction(startServer)}
                          disabled={actionLoading || status === 'running'}
                          startIcon={<PlayArrowIcon />}
                          fullWidth
                          sx={{ py: 1.2 }}
                        >
                          Start
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={() => handleAction(stopServer)}
                          disabled={actionLoading || status !== 'running'}
                          startIcon={<StopIcon />}
                          fullWidth
                          sx={{ py: 1.2 }}
                        >
                          Stop
                        </Button>
                      </Box>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

                      <Button variant="outlined" component="label" disabled={actionLoading} startIcon={<UploadFileIcon />} sx={{ justifyContent: 'flex-start', py: 1 }}>
                        Upload Mod/Plugin
                        <input type="file" hidden onChange={handleFileUpload} />
                      </Button>

                      <Button variant="outlined" component="label" color="secondary" disabled={actionLoading || datapackUploading} startIcon={<CloudUploadIcon />} sx={{ justifyContent: 'flex-start', py: 1 }}>
                        {datapackUploading ? 'Uploading...' : 'Upload Datapack'}
                        <input type="file" hidden onChange={handleDatapackUpload} />
                      </Button>

                      <Button variant="outlined" onClick={() => setCurseForgeOpen(true)} disabled={actionLoading} startIcon={<AddCircleOutlineIcon />} sx={{ justifyContent: 'flex-start', py: 1 }}>
                        CurseForge Hub
                      </Button>
                      <Button variant="outlined" onClick={() => setEditOpen(true)} disabled={actionLoading} startIcon={<EditIcon />} sx={{ justifyContent: 'flex-start', py: 1 }}>
                        Edit Settings
                      </Button>
                      <Button variant="outlined" color="error" onClick={handleDeleteServer} disabled={actionLoading} startIcon={<DeleteIcon />} sx={{ justifyContent: 'flex-start', py: 1 }}>
                        Delete Server
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Main Tabs Area */}
            <Grid item xs={12} lg={9}>
              <Paper sx={{
                borderRadius: 4,
                border: '1px solid rgba(255, 255, 255, 0.08)',
                background: 'rgba(20, 20, 30, 0.4)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
                overflow: 'hidden'
              }}>
                <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.08)', bgcolor: 'rgba(0,0,0,0.15)', px: 2 }}>
                  <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} variant="scrollable" scrollButtons="auto" textColor="primary" indicatorColor="primary">
                    <Tab label="Interactive Terminal" icon={<TerminalIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 'bold', fontFamily: 'Outfit', py: 2 }} />
                    <Tab label="File Explorer" icon={<FolderIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 'bold', fontFamily: 'Outfit', py: 2 }} />
                    <Tab label="Backups" icon={<StorageIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 'bold', fontFamily: 'Outfit', py: 2 }} />
                    <Tab label="Config Details" icon={<SettingsIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 'bold', fontFamily: 'Outfit', py: 2 }} />
                    <Tab label="Mods List" icon={<ExtensionIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 'bold', fontFamily: 'Outfit', py: 2 }} />
                  </Tabs>
                </Box>

                <Box sx={{ px: 3, pb: 2 }}>
                  {/* TAB 1: Live terminal + Player manager */}
                  <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                      {/* Terminal screen */}
                      <Grid item xs={12} md={7.5}>
                        <Card sx={{ height: '100%', bgcolor: '#060609', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
                          <CardHeader
                            title="Interactive RCON Console"
                            titleTypographyProps={{ variant: 'subtitle2', sx: { fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' } }}
                            action={
                              <Chip
                                label={status === 'running' ? 'LIVE' : 'OFFLINE'}
                                size="small"
                                sx={{
                                  bgcolor: status === 'running' ? 'rgba(0,230,118,0.1)' : 'rgba(255,255,255,0.05)',
                                  color: status === 'running' ? '#00E676' : 'text.secondary',
                                  fontSize: '0.65rem'
                                }}
                              />
                            }
                          />
                          <Box
                            ref={logContainerRef}
                            onScroll={handleScroll}
                            sx={{
                              height: '420px',
                              bgcolor: '#030305',
                              color: '#eceef5',
                              p: 2.5,
                              overflowY: 'auto',
                              fontFamily: 'Fira Code, Consolas, monospace',
                              fontSize: '0.8rem',
                              lineHeight: '1.6',
                              borderTop: '1px solid rgba(255,255,255,0.05)',
                              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.05)' }
                            }}
                          >
                            {logs.length > 0 ? logs.map((line, i) => (
                              <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: '4px' }}>{line}</div>
                            )) : (
                              <Typography color="text.secondary" sx={{ fontStyle: 'italic', opacity: 0.5, fontSize: '0.8rem' }}>
                                Console initialized. Waiting for stream input logs...
                              </Typography>
                            )}
                            <div ref={logEndRef} />
                          </Box>

                          {/* Console command Form */}
                          <Box
                            component="form"
                            onSubmit={handleSendCommand}
                            sx={{
                              p: 2,
                              bgcolor: '#08080c',
                              display: 'flex',
                              gap: 1.5,
                              alignItems: 'center',
                              borderTop: '1px solid rgba(255,255,255,0.05)'
                            }}
                          >
                            <TextField
                              size="small"
                              placeholder={status === 'running' ? 'Type console RCON command...' : 'RCON offline'}
                              variant="outlined"
                              fullWidth
                              value={commandText}
                              onChange={(e) => setCommandText(e.target.value)}
                              disabled={status !== 'running' || commandSubmitting}
                              sx={{
                                input: {
                                  color: '#fff',
                                  fontFamily: 'Fira Code, Consolas, monospace',
                                  fontSize: '0.8rem'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                                }
                              }}
                            />
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              disabled={status !== 'running' || !commandText.trim() || commandSubmitting}
                              sx={{ py: 1, px: 2.5 }}
                            >
                              <SendIcon fontSize="small" />
                            </Button>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Right: Players & Access control Administration */}
                      <Grid item xs={12} md={4.5}>
                        <Stack spacing={3}>
                          {/* Active Players lists table */}
                          <Card sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                            <CardHeader title="Online Administration" titleTypographyProps={{ variant: 'subtitle2', sx: { fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' } }} />
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                            <CardContent sx={{ p: 0 }}>
                              {status === 'running' && stats?.players?.list && stats.players.list.length > 0 ? (
                                <Table size="small">
                                  <TableBody>
                                    {stats.players.list.map((player) => (
                                      <TableRow key={player}>
                                        <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', pl: 2 }}>
                                          <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.dark' }}>{player.charAt(0).toUpperCase()}</Avatar>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{player}</Typography>
                                          </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                          <Tooltip title="Kick Player">
                                            <IconButton size="small" color="warning" onClick={() => handleKickPlayer(player)}>
                                              <StopIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Ban Player">
                                            <IconButton size="small" color="error" onClick={() => handleBanPlayer(player)}>
                                              <GavelIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 3, fontStyle: 'italic', textAlign: 'center' }}>No active online players</Typography>
                              )}
                            </CardContent>
                          </Card>

                          {/* Access Control Manager lists */}
                          <Card sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                            <CardHeader
                              title="Access Control Lists"
                              titleTypographyProps={{ variant: 'subtitle2', sx: { fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' } }}
                              avatar={<SecurityIcon fontSize="small" color="action" />}
                            />
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                            <CardContent sx={{ p: 2 }}>
                              <Stack spacing={2.5}>
                                {/* Whitelist additions */}
                                <Box component="form" onSubmit={handleAddWhitelist}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', fontFamily: 'Outfit' }}>
                                    WHITELIST PLAYER
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      size="small"
                                      placeholder="Username..."
                                      fullWidth
                                      value={whitelistUser}
                                      onChange={(e) => setWhitelistUser(e.target.value)}
                                      disabled={whitelistLoading}
                                    />
                                    <Button type="submit" variant="contained" color="primary" size="small" disabled={!whitelistUser.trim() || whitelistLoading}>
                                      Add
                                    </Button>
                                  </Box>
                                </Box>

                                {/* Operator promotions */}
                                <Box component="form" onSubmit={handleAddOp}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', fontFamily: 'Outfit' }}>
                                    OPERATORS (OPs) PROMOTION
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      size="small"
                                      placeholder="Username..."
                                      fullWidth
                                      value={opUser}
                                      onChange={(e) => setOpUser(e.target.value)}
                                      disabled={opLoading}
                                    />
                                    <Button type="submit" variant="contained" color="secondary" size="small" disabled={!opUser.trim() || opLoading}>
                                      OP
                                    </Button>
                                  </Box>
                                </Box>

                                {/* List display */}
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                                    ACTIVE OPERATORS ({opUsers.length})
                                  </Typography>
                                  {opUsers.length > 0 ? (
                                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                      {opUsers.map(op => (
                                        <Chip
                                          key={op.uuid || op.name}
                                          label={op.name}
                                          size="small"
                                          onDelete={() => handleRemoveOp(op.name)}
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No operators registered</Typography>
                                  )}
                                </Box>

                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                                    WHITELISTED ({whitelistUsers.length})
                                  </Typography>
                                  {whitelistUsers.length > 0 ? (
                                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                      {whitelistUsers.map(wl => (
                                        <Chip
                                          key={wl.uuid || wl.name}
                                          label={wl.name}
                                          size="small"
                                          onDelete={() => handleRemoveWhitelist(wl.name)}
                                          color="primary"
                                          variant="outlined"
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Whitelist is empty</Typography>
                                  )}
                                </Box>

                              </Stack>
                            </CardContent>
                          </Card>
                        </Stack>
                      </Grid>
                    </Grid>
                  </TabPanel>

                  {/* TAB 2: File Explorer */}
                  <TabPanel value={tabValue} index={1}>
                    <FileManager serverName={serverName} />
                  </TabPanel>

                  {/* TAB 3: Backups */}
                  <TabPanel value={tabValue} index={2}>
                    <BackupManager serverName={serverName} status={status} />
                  </TabPanel>

                  {/* TAB 4: Config Details */}
                  <TabPanel value={tabValue} index={3}>
                    <Grid container spacing={2}>
                      {displaySettings.map(([key, val]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Box sx={{
                            bgcolor: 'rgba(0,0,0,0.15)',
                            p: 1.5,
                            px: 2,
                            borderRadius: 2.5,
                            border: '1px solid rgba(255,255,255,0.04)',
                            height: '100%'
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Outfit', fontWeight: 'bold' }}>
                              {key}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                              {val}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </TabPanel>

                  {/* TAB 5: Installed Mods List */}
                  <TabPanel value={tabValue} index={4}>
                    <Card sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                      <CardContent>
                        {server.mods && server.mods.length > 0 ? (
                          <Grid container spacing={2}>
                            {server.mods.map((mod, index) => (
                              <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper sx={{
                                  p: 1.5,
                                  px: 2,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  bgcolor: 'rgba(255,255,255,0.01)',
                                  border: '1px solid rgba(255,255,255,0.04)',
                                  borderRadius: 2
                                }}>
                                  <Typography variant="body2" sx={{ noWrap: true, fontWeight: 'bold', fontSize: '0.85rem' }}>{mod}</Typography>
                                  <IconButton edge="end" aria-label="delete" size="small" color="error" onClick={() => handleDeleteMod(mod)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                            No mods or plugins installed on this server instance.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </TabPanel>
                </Box>
              </Paper>
            </Grid>

          </Grid>

          <EditServerDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            server={server}
            onUpdate={fetchData}
          />

          <CurseForgeBrowser
            open={curseForgeOpen}
            onClose={() => setCurseForgeOpen(false)}
            serverName={serverName}
            serverVersion={server.server_settings.VERSION === 'LATEST' ? null : server.server_settings.VERSION}
            serverType={server.server_settings.TYPE}
            installedMods={server.mods || []}
            onInstall={fetchData}
          />
        </Box>
      </Fade>
    </Container>
  );
}